import { emptyishToNull } from '@snap-types/schemas/emptyish';
import {
  decimal,
  nullableDecimal,
  nullableInt,
  nullableString,
  requiredInt,
  yesNoBoolean,
} from '@snap-types/schemas/emptyish-coercions';
import { z } from 'zod/v4';

/**
 * Sentinel values emitted by the spreadsheet that drive conditional parsing
 * and requiredness. We keep exact casing here to normalize inputs and to
 * avoid scattering string literals across the schema.
 */
export const CONSULT_ENGINEERING = 'Consult Engineering';
export const ENGINEERING_REVIEW = 'ER';
export const NO_OPERATION = 'No';

/**
 * Normalize known sentinel strings so downstream checks only have to compare
 * against a single canonical value.
 */
const normalizedOperationValue = (value: unknown) => {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();

  if (trimmed.toLowerCase() === CONSULT_ENGINEERING.toLowerCase()) {
    return CONSULT_ENGINEERING;
  }

  if (trimmed.toLowerCase() === NO_OPERATION.toLowerCase()) {
    return NO_OPERATION;
  }

  return value;
};

export function normalizeConsultEngineeringValue(value: unknown) {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return value;
  }

  if (
    trimmed.toLowerCase() === CONSULT_ENGINEERING.toLowerCase() ||
    trimmed.toLowerCase() === ENGINEERING_REVIEW.toLowerCase()
  ) {
    return null;
  }

  return value;
}

export function normalizeConsultEngineeringSelections(raw: unknown) {
  if (!raw || typeof raw !== 'object') {
    return raw;
  }

  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).map(([key, value]) => [
      key,
      normalizeConsultEngineeringValue(value),
    ]),
  );
}

/**
 * Determines whether an operation is effectively disabled, which allows us to
 * null out dependent fields before validation.
 */
export function isDisabledOperation(value: unknown) {
  if (typeof value !== 'string') {
    return value == null;
  }

  const normalized = value.trim().toLowerCase();
  return (
    normalized.length === 0 ||
    normalized === NO_OPERATION.toLowerCase() ||
    normalized === CONSULT_ENGINEERING.toLowerCase()
  );
}

/**
 * Spreadsheet rows allow operations to be marked as "No" or
 * "Consult Engineering". This pre-pass lifts those flags into a consistent
 * shape so validation can enforce requiredness only when the operation is
 * active.
 */
const normalizeOperationSelections = (raw: unknown) => {
  if (!raw || typeof raw !== 'object') {
    return raw;
  }

  const data = { ...(raw as Record<string, unknown>) };

  const normalizeSelection = (key: string) => {
    data[key] = normalizedOperationValue(data[key]);
  };

  normalizeSelection('headingOperation');
  normalizeSelection('pinchOperation');
  normalizeSelection('rollingOperation');
  normalizeSelection('trimmingOperation');
  normalizeSelection('shankSlottingOperation');
  normalizeSelection('sawSlottingOperation');

  if (isConsultEngineering(data.dephosRequired)) {
    data.dephosRequired = null;
  }

  if (isConsultEngineering(data.heatTreatType)) {
    data.heatTreatType = null;
  }

  if (isNoOperation(data.heatTreatType)) {
    data.heatTreatType = null;
  }

  if (isDisabledOperation(data.headingOperation)) {
    data.headingRate = null;
    data.headingSetupHours = null;
  }

  if (isDisabledOperation(data.pinchOperation)) {
    data.pinchRate = null;
    data.pinchSetupHours = null;
  }

  if (isDisabledOperation(data.rollingOperation)) {
    data.rollingRate = null;
    data.rollingSetupHours = null;
  }

  if (isDisabledOperation(data.trimmingOperation)) {
    data.trimmingRate = null;
    data.trimmingSetupHours = null;
  }

  if (isDisabledOperation(data.shankSlottingOperation)) {
    data.shankSlottingRate = null;
    data.shankSlottingSetupHours = null;
  }

  return data;
};

/**
 * Base schema matches the spreadsheet column mapping. Conditional requiredness
 * is handled in a separate check step to keep the field definitions readable.
 */
const baseAlphaQuoteLineImportSchema = z.object({
  description: z.string({ error: 'Description is required' }).min(1),
  // Populates Visual's WORK_ORDER.DRAWING_ID/DRAWING_REV_NO (nvarchar(30)/(8))
  // once a snap work order is built from this line - reject at import time
  // rather than let an oversized value fail later at Visual-send time.
  drawingId: z.preprocess(
    emptyishToNull,
    z.string().max(30, 'Drawing ID must be 30 characters or less').nullish(),
  ),
  drawingRevisionNo: z.preprocess(
    emptyishToNull,
    z
      .string()
      .max(8, 'Drawing Revision No must be 8 characters or less')
      .nullish(),
  ),
  notes: nullableString,
  customerPartId: nullableString,

  fastenerType: z.string({ error: 'Fastener type is required' }).min(1),
  material: z.coerce.string({ error: 'Material is required' }).min(1),
  dfars: yesNoBoolean.transform((v) => v ?? false),
  threadSize: z.string({ error: 'Thread size is required' }).min(1),
  threadPitch: z.string({ error: 'Thread pitch is required' }).min(1),
  length: decimal,

  shoulderDiameter: nullableDecimal,
  shoulderLength: nullableDecimal,

  headStyle: z.string({ error: 'Head style is required' }).min(1),
  recess: z.string({ error: 'Recess is required' }).min(1),
  serration: yesNoBoolean.transform((v) => v ?? false),

  surfaceFinish: z.string({ error: 'Surface finish is required' }).min(1),
  postFinish: z.string({ error: 'Post finish is required' }).min(1),

  sems1Style: nullableString,
  sems1Weight: nullableDecimal,
  sems2Style: nullableString,
  sems2Weight: nullableDecimal,

  osp2: nullableString,
  osp2Description: nullableString,
  osp2UOM: nullableString,
  osp2MinLot: nullableDecimal,

  osp3: nullableString,
  osp3Description: nullableString,
  osp3UOM: nullableString,
  osp3MinLot: nullableDecimal,

  baseMaterial: z.coerce.string({ error: 'Base material is required' }).min(1),
  thread: z.string({ error: 'Thread is required' }).min(1),

  preTrimHeadVolume: nullableDecimal,
  finalHeadVolume: decimal,
  shoulderVolume: decimal,
  threadVolume: decimal,
  preTrimVolume: decimal,
  finalVolume: decimal,

  preTrimWeight: decimal,
  finalWeight: decimal,

  rawMaterialSize: decimal,
  cutoffLength: decimal,
  diametersInHead: decimal,
  threadBlankDiameter: decimal,
  reductionInArea: decimal,

  headingOperation: z.string({ error: 'Heading operation is required' }).min(1),
  headingRate: nullableDecimal,
  headingSetupHours: nullableDecimal,

  pinchOperation: nullableString,
  pinchRate: nullableDecimal,
  pinchSetupHours: nullableDecimal,

  rollingOperation: z.string({ error: 'Rolling operation is required' }).min(1),
  rollingRate: nullableDecimal,
  rollingSetupHours: nullableDecimal,

  trimmingOperation: nullableString,
  trimmingRate: nullableDecimal,
  trimmingSetupHours: nullableDecimal,

  shankSlottingOperation: nullableString,
  shankSlottingRate: nullableDecimal,
  shankSlottingSetupHours: nullableDecimal,

  dephosRequired: yesNoBoolean.pipe(
    z.boolean({ error: 'Dephos required must be true or false' }),
  ),

  heatTreatType: nullableString,

  driveStyleAdder: decimal,
  threadFormingAdder: decimal,
  matThreadAdder: decimal,

  materialLeadTimeWeeks: requiredInt,
  headingLeadTimeWeeks: requiredInt,
  pinchPointLeadTimeWeeks: nullableInt,
  rollingLeadTimeWeeks: requiredInt,
  trimmingLeadTimeWeeks: nullableInt,
  shankSlottingLeadTimeWeeks: nullableInt,
  dephosLeadTimeWeeks: nullableInt,
  heatTreatLeadTimeWeeks: nullableInt,
  finishingLeadTimeWeeks: requiredInt,
  osp2LeadTimeWeeks: nullableInt,
  osp3LeadTimeWeeks: nullableInt,
  orderEntryLeadTimeWeeks: requiredInt,
  toolingLeadTimeWeeks: requiredInt,

  leadTimeWeeks: requiredInt,
});

/**
 * Full import schema: normalize operation selections, enforce conditional
 * requirements, then apply default numeric values used for persistence.
 */
export const alphaQuoteLineImportSchema = z
  .preprocess(normalizeOperationSelections, baseAlphaQuoteLineImportSchema)
  .check((ctx) => {
    const data = ctx.value;

    if (!isDisabledOperation(data.headingOperation)) {
      if (data.headingRate == null) {
        ctx.issues.push({
          code: 'custom',
          path: ['headingRate'],
          message: 'Heading rate is required',
          input: data.headingRate,
        });
      }

      if (data.headingSetupHours == null) {
        ctx.issues.push({
          code: 'custom',
          path: ['headingSetupHours'],
          message: 'Heading setup hours are required',
          input: data.headingSetupHours,
        });
      }
    }

    if (!isDisabledOperation(data.pinchOperation)) {
      if (data.pinchRate == null) {
        ctx.issues.push({
          code: 'custom',
          path: ['pinchRate'],
          message: 'Pinch rate is required',
          input: data.pinchRate,
        });
      }

      if (data.pinchSetupHours == null) {
        ctx.issues.push({
          code: 'custom',
          path: ['pinchSetupHours'],
          message: 'Pinch setup hours are required',
          input: data.pinchSetupHours,
        });
      }
    }

    if (!isDisabledOperation(data.rollingOperation)) {
      if (data.rollingRate == null) {
        ctx.issues.push({
          code: 'custom',
          path: ['rollingRate'],
          message: 'Rolling rate is required',
          input: data.rollingRate,
        });
      }

      if (data.rollingSetupHours == null) {
        ctx.issues.push({
          code: 'custom',
          path: ['rollingSetupHours'],
          message: 'Rolling setup hours are required',
          input: data.rollingSetupHours,
        });
      }
    }

    if (!isDisabledOperation(data.trimmingOperation)) {
      if (data.trimmingRate == null) {
        ctx.issues.push({
          code: 'custom',
          path: ['trimmingRate'],
          message: 'Trimming rate is required',
          input: data.trimmingRate,
        });
      }

      if (data.trimmingSetupHours == null) {
        ctx.issues.push({
          code: 'custom',
          path: ['trimmingSetupHours'],
          message: 'Trimming setup hours are required',
          input: data.trimmingSetupHours,
        });
      }
    }

    if (!isDisabledOperation(data.shankSlottingOperation)) {
      if (data.shankSlottingRate == null) {
        ctx.issues.push({
          code: 'custom',
          path: ['shankSlottingRate'],
          message: 'Shank slotting rate is required',
          input: data.shankSlottingRate,
        });
      }

      if (data.shankSlottingSetupHours == null) {
        ctx.issues.push({
          code: 'custom',
          path: ['shankSlottingSetupHours'],
          message: 'Shank slotting setup hours are required',
          input: data.shankSlottingSetupHours,
        });
      }
    }
  })
  .transform((data) => ({
    ...data,
    headingRate: data.headingRate ?? 0,
    headingSetupHours: data.headingSetupHours ?? 0,
    rollingRate: data.rollingRate ?? 0,
    rollingSetupHours: data.rollingSetupHours ?? 0,
  }));

export type AlphaQuoteLineImport = z.output<typeof alphaQuoteLineImportSchema>;

const consultEngineeringAlphaQuoteLineImportSchemaBase = z.object({
  ...baseAlphaQuoteLineImportSchema.shape,
  baseMaterial: z.preprocess(emptyishToNull, z.coerce.string().nullish()),
  thread: nullableString,
  finalHeadVolume: nullableDecimal,
  shoulderVolume: nullableDecimal,
  threadVolume: nullableDecimal,
  preTrimVolume: nullableDecimal,
  finalVolume: nullableDecimal,
  preTrimWeight: nullableDecimal,
  finalWeight: nullableDecimal,
  rawMaterialSize: nullableDecimal,
  cutoffLength: nullableDecimal,
  diametersInHead: nullableDecimal,
  threadBlankDiameter: nullableDecimal,
  reductionInArea: nullableDecimal,
  headingOperation: nullableString,
  headingRate: nullableDecimal,
  headingSetupHours: nullableDecimal,
  rollingOperation: nullableString,
  rollingRate: nullableDecimal,
  rollingSetupHours: nullableDecimal,
  dephosRequired: yesNoBoolean,
  driveStyleAdder: nullableDecimal,
  threadFormingAdder: nullableDecimal,
  matThreadAdder: nullableDecimal,
  materialLeadTimeWeeks: nullableInt,
  headingLeadTimeWeeks: nullableInt,
  rollingLeadTimeWeeks: nullableInt,
  finishingLeadTimeWeeks: nullableInt,
  orderEntryLeadTimeWeeks: nullableInt,
  toolingLeadTimeWeeks: nullableInt,
  leadTimeWeeks: nullableInt,
});

export const alphaQuoteLineConsultEngineeringImportSchema = z.preprocess(
  (raw) =>
    normalizeOperationSelections(normalizeConsultEngineeringSelections(raw)),
  consultEngineeringAlphaQuoteLineImportSchemaBase,
);

export type AlphaQuoteLineConsultEngineeringImport = z.output<
  typeof alphaQuoteLineConsultEngineeringImportSchema
>;

export function isConsultEngineering(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    value.trim().toLowerCase() === CONSULT_ENGINEERING.toLowerCase()
  );
}

export function isNoOperation(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    value.trim().toLowerCase() === NO_OPERATION.toLowerCase()
  );
}
